import { Chat } from '@ai-sdk/react';
import {
  DefaultChatTransport,
  type ChatInit,
} from 'ai';
import { apiBaseUrl } from '@/lib/auth-client';
import type { ToolContinuationErrorState } from './chat-ask-user-flow';
import type { ChatTurnErrorState } from './chat-turn-error';
import type { ChatUIMessage } from './types';

const DEFAULT_NEW_CHAT_KEY = 'default';
const DEFAULT_EVICTION_DELAY_MS = 5 * 60 * 1000;

type ChatHandlers = Pick<
  ChatInit<ChatUIMessage>,
  'onData' | 'onError' | 'onFinish' | 'sendAutomaticallyWhen'
>;

interface MutableChatHandlers {
  current: ChatHandlers;
}

export interface ChatStreamRuntimeState {
  isAskUserSubmitting: boolean;
  isStopRequested: boolean;
  toolContinuationError: ToolContinuationErrorState | null;
  turnError: ChatTurnErrorState | null;
}

export interface ChatStreamRegistryEntry {
  readonly chat: Chat<ChatUIMessage>;
  readonly isPlaceholder: boolean;
  conversationId?: string;
  readonly getActiveTurnId: () => string | null;
  readonly getRequestTurnId: () => string | null;
  readonly getLastAskUserSubmission: () => ToolContinuationErrorState | null;
  readonly getRuntimeSnapshot: () => ChatStreamRuntimeState;
  readonly replaceMessages: (
    update:
      | ChatUIMessage[]
      | ((current: ChatUIMessage[]) => ChatUIMessage[]),
  ) => void;
  readonly setHandlers: (handlers: ChatHandlers) => void;
  readonly setLastAskUserSubmission: (
    submission: ToolContinuationErrorState | null,
  ) => void;
  readonly subscribeRuntime: (listener: () => void) => () => void;
  readonly updateRuntime: (
    update:
      | Partial<ChatStreamRuntimeState>
      | ((current: ChatStreamRuntimeState) => ChatStreamRuntimeState),
  ) => void;
}

interface InternalChatStreamRegistryEntry extends ChatStreamRegistryEntry {
  evictionTimer: ReturnType<typeof setTimeout> | null;
  handlers: MutableChatHandlers;
  key: string;
  lastAskUserSubmission: ToolContinuationErrorState | null;
  lastHiddenAt: number | null;
  runtimeState: ChatStreamRuntimeState;
  runtimeSubscribers: Set<() => void>;
  requestTurnId: string | null;
  turnActive: boolean;
}

interface ConversationChatRegistryOptions {
  clearTimer?: (timer: ReturnType<typeof setTimeout>) => void;
  createChat?: (init: ChatInit<ChatUIMessage>) => Chat<ChatUIMessage>;
  evictionDelayMs?: number;
  now?: () => number;
  setTimer?: (
    callback: () => void,
    delayMs: number,
  ) => ReturnType<typeof setTimeout>;
}

interface SelectChatEntryOptions {
  conversationId?: string;
  initialMessages: ChatUIMessage[];
  initialMessagesReady: boolean;
  newChatKey?: string | null;
  ownerId?: string;
}

const EMPTY_HANDLERS: ChatHandlers = {};
const INITIAL_RUNTIME_STATE: ChatStreamRuntimeState = {
  isAskUserSubmitting: false,
  isStopRequested: false,
  toolContinuationError: null,
  turnError: null,
};

function createMessageId() {
  return crypto.randomUUID();
}

function createDefaultChat(init: ChatInit<ChatUIMessage>) {
  return new Chat<ChatUIMessage>(init);
}

function getConversationKey(conversationId: string) {
  return `conversation:${conversationId}`;
}

function getNewChatKey(newChatKey?: string | null) {
  return `new:${newChatKey ?? DEFAULT_NEW_CHAT_KEY}`;
}

function getPlaceholderKey(conversationId: string) {
  return `placeholder:${conversationId}`;
}

export class ConversationChatRegistry {
  readonly #clearTimer: (timer: ReturnType<typeof setTimeout>) => void;
  readonly #createChat: (init: ChatInit<ChatUIMessage>) => Chat<ChatUIMessage>;
  readonly #entries = new Map<string, InternalChatStreamRegistryEntry>();
  readonly #evictionDelayMs: number;
  readonly #now: () => number;
  readonly #setTimer: (
    callback: () => void,
    delayMs: number,
  ) => ReturnType<typeof setTimeout>;
  #displayedEntry: InternalChatStreamRegistryEntry | null = null;
  #ownerId: string | null = null;

  constructor(options: ConversationChatRegistryOptions = {}) {
    this.#clearTimer = options.clearTimer
      ?? ((timer) => globalThis.clearTimeout(timer));
    this.#createChat = options.createChat ?? createDefaultChat;
    this.#evictionDelayMs = options.evictionDelayMs ?? DEFAULT_EVICTION_DELAY_MS;
    this.#now = options.now ?? Date.now;
    this.#setTimer = options.setTimer
      ?? ((callback, delayMs) => globalThis.setTimeout(callback, delayMs));
  }

  get size() {
    return [...this.#entries.values()].filter((entry) => !entry.isPlaceholder).length;
  }

  select(options: SelectChatEntryOptions): ChatStreamRegistryEntry {
    this.#selectOwner(options.ownerId);

    if (options.conversationId) {
      const conversationKey = getConversationKey(options.conversationId);
      const existingEntry = this.#entries.get(conversationKey);

      if (existingEntry) {
        return existingEntry;
      }

      if (!options.initialMessagesReady) {
        const placeholderEntry = this.#getOrCreateEntry({
          initialMessages: [],
          isPlaceholder: true,
          key: getPlaceholderKey(options.conversationId),
        });
        return placeholderEntry;
      }

      this.#deleteEntry(getPlaceholderKey(options.conversationId));
      const conversationEntry = this.#getOrCreateEntry({
        conversationId: options.conversationId,
        initialMessages: options.initialMessages,
        isPlaceholder: false,
        key: conversationKey,
      });
      return conversationEntry;
    }

    const newChatEntry = this.#getOrCreateEntry({
      initialMessages: options.initialMessages,
      isPlaceholder: false,
      key: getNewChatKey(options.newChatKey),
    });
    return newChatEntry;
  }

  getConversationEntry(conversationId: string): ChatStreamRegistryEntry | undefined {
    return this.#entries.get(getConversationKey(conversationId));
  }

  isDisplayed(entry: ChatStreamRegistryEntry) {
    return this.#displayedEntry === entry;
  }

  markDisplayed(entry: ChatStreamRegistryEntry) {
    const internalEntry = this.#asInternalEntry(entry);

    if (this.#displayedEntry === internalEntry) {
      this.#cancelEviction(internalEntry);
      return;
    }

    if (this.#displayedEntry) {
      this.#markHidden(this.#displayedEntry);
    }

    this.#displayedEntry = internalEntry;
    internalEntry.lastHiddenAt = null;
    this.#cancelEviction(internalEntry);
  }

  releaseDisplayed(entry: ChatStreamRegistryEntry) {
    const internalEntry = this.#asInternalEntry(entry);

    if (this.#displayedEntry !== internalEntry) {
      return;
    }

    this.#displayedEntry = null;
    this.#markHidden(internalEntry);
  }

  reindex(entry: ChatStreamRegistryEntry, conversationId: string) {
    const internalEntry = this.#asInternalEntry(entry);
    const nextKey = getConversationKey(conversationId);

    if (internalEntry.key === nextKey) {
      internalEntry.conversationId = conversationId;
      return;
    }

    const conflictingEntry = this.#entries.get(nextKey);
    if (conflictingEntry && conflictingEntry !== internalEntry) {
      this.#deleteEntry(nextKey);
    }

    this.#entries.delete(internalEntry.key);
    internalEntry.key = nextKey;
    internalEntry.conversationId = conversationId;
    this.#entries.set(nextKey, internalEntry);
  }

  tryAcquireTurn(entry: ChatStreamRegistryEntry) {
    const internalEntry = this.#asInternalEntry(entry);

    if (internalEntry.turnActive) {
      return false;
    }

    if (this.activeTurnCount >= 3) {
      internalEntry.requestTurnId = null;
      return false;
    }

    internalEntry.requestTurnId ??= crypto.randomUUID();
    internalEntry.turnActive = true;
    return true;
  }

  prepareTurn(entry: ChatStreamRegistryEntry) {
    const internalEntry = this.#asInternalEntry(entry);
    internalEntry.requestTurnId ??= crypto.randomUUID();
    return internalEntry.requestTurnId;
  }

  releaseTurn(entry: ChatStreamRegistryEntry) {
    const internalEntry = this.#asInternalEntry(entry);
    internalEntry.requestTurnId = null;
    internalEntry.turnActive = false;

    if (
      this.#entries.get(internalEntry.key) === internalEntry
      && !this.isDisplayed(entry)
    ) {
      this.#scheduleEviction(internalEntry);
    }
  }

  get activeTurnCount() {
    let count = 0;

    for (const entry of this.#entries.values()) {
      if (!entry.isPlaceholder && entry.turnActive) {
        count += 1;
      }
    }

    return count;
  }

  resetEntry(entry: ChatStreamRegistryEntry) {
    const internalEntry = this.#asInternalEntry(entry);

    if (internalEntry.turnActive) {
      return;
    }

    internalEntry.chat.clearError();
    internalEntry.chat.messages = [];
    internalEntry.lastAskUserSubmission = null;
    internalEntry.updateRuntime(INITIAL_RUNTIME_STATE);
  }

  dispose() {
    for (const entry of this.#entries.values()) {
      this.#cancelEviction(entry);
    }

    this.#entries.clear();
    this.#displayedEntry = null;
    this.#ownerId = null;
  }

  #getOrCreateEntry(params: {
    conversationId?: string;
    initialMessages: ChatUIMessage[];
    isPlaceholder: boolean;
    key: string;
  }) {
    const existingEntry = this.#entries.get(params.key);
    if (existingEntry) {
      return existingEntry;
    }

    const handlers: MutableChatHandlers = { current: EMPTY_HANDLERS };
    const runtimeSubscribers = new Set<() => void>();
    const entryRef: { current: InternalChatStreamRegistryEntry | null } = {
      current: null,
    };
    const getEntry = () => {
      if (!entryRef.current) {
        throw new Error('Chat registry entry is not initialized.');
      }

      return entryRef.current;
    };

    // Chat conserve ces callbacks pendant toute sa vie. Ils ne capturent donc
    // que l'entrée stable et délèguent les valeurs React au porteur mutable.
    const chat = this.#createChat({
      generateId: createMessageId,
      messages: params.initialMessages,
      onData: (dataPart) => {
        const entry = getEntry();
        if (dataPart.type === 'data-conversation') {
          this.reindex(entry, dataPart.data.conversationId);
        }

        return entry.handlers.current.onData?.(dataPart);
      },
      onError: (error) => getEntry().handlers.current.onError?.(error),
      onFinish: (event) => {
        const entry = getEntry();
        this.releaseTurn(entry);
        entry.handlers.current.onFinish?.(event);
      },
      sendAutomaticallyWhen: (options) => (
        getEntry().handlers.current.sendAutomaticallyWhen?.(options) ?? false
      ),
      transport: new DefaultChatTransport({
        api: `${apiBaseUrl}/api/chat`,
        credentials: 'include',
      }),
    });

    const entry: InternalChatStreamRegistryEntry = {
      chat,
      conversationId: params.conversationId,
      evictionTimer: null,
      getActiveTurnId: () => (
        entry.turnActive ? entry.requestTurnId : null
      ),
      getLastAskUserSubmission: () => entry.lastAskUserSubmission,
      getRequestTurnId: () => entry.requestTurnId,
      getRuntimeSnapshot: () => entry.runtimeState,
      handlers,
      isPlaceholder: params.isPlaceholder,
      key: params.key,
      lastAskUserSubmission: null,
      lastHiddenAt: null,
      runtimeState: INITIAL_RUNTIME_STATE,
      runtimeSubscribers,
      requestTurnId: null,
      replaceMessages: (update) => {
        entry.chat.messages = typeof update === 'function'
          ? update(entry.chat.messages)
          : update;
      },
      setHandlers: (nextHandlers) => {
        entry.handlers.current = nextHandlers;
      },
      setLastAskUserSubmission: (submission) => {
        entry.lastAskUserSubmission = submission;
      },
      subscribeRuntime: (listener) => {
        entry.runtimeSubscribers.add(listener);
        return () => {
          entry.runtimeSubscribers.delete(listener);
        };
      },
      turnActive: false,
      updateRuntime: (update) => {
        const nextState = typeof update === 'function'
          ? update(entry.runtimeState)
          : { ...entry.runtimeState, ...update };

        if (Object.is(nextState, entry.runtimeState)) {
          return;
        }

        entry.runtimeState = nextState;
        entry.runtimeSubscribers.forEach((listener) => listener());
      },
    };

    entryRef.current = entry;
    this.#entries.set(params.key, entry);
    return entry;
  }

  #asInternalEntry(entry: ChatStreamRegistryEntry) {
    return entry as InternalChatStreamRegistryEntry;
  }

  #markHidden(entry: InternalChatStreamRegistryEntry) {
    entry.lastHiddenAt = this.#now();
    this.#scheduleEviction(entry);
  }

  #selectOwner(ownerId?: string) {
    const nextOwnerId = ownerId ?? '';

    if (this.#ownerId === null) {
      this.#ownerId = nextOwnerId;
      return;
    }

    if (this.#ownerId === nextOwnerId) {
      return;
    }

    for (const entry of this.#entries.values()) {
      this.#cancelEviction(entry);
      entry.handlers.current = EMPTY_HANDLERS;

      if (
        entry.turnActive
        || entry.chat.status === 'streaming'
        || entry.chat.status === 'submitted'
      ) {
        void entry.chat.stop();
      }
    }

    this.#entries.clear();
    this.#displayedEntry = null;
    this.#ownerId = nextOwnerId;
  }

  #scheduleEviction(entry: InternalChatStreamRegistryEntry) {
    this.#cancelEviction(entry);

    if (this.#displayedEntry === entry || entry.turnActive) {
      return;
    }

    const hiddenAt = entry.lastHiddenAt ?? this.#now();
    const elapsedMs = Math.max(0, this.#now() - hiddenAt);
    const delayMs = Math.max(0, this.#evictionDelayMs - elapsedMs);

    entry.evictionTimer = this.#setTimer(() => {
      entry.evictionTimer = null;

      if (
        this.#displayedEntry === entry
        || entry.turnActive
        || entry.chat.status === 'streaming'
        || entry.chat.status === 'submitted'
      ) {
        return;
      }

      this.#deleteEntry(entry.key);
    }, delayMs);
  }

  #cancelEviction(entry: InternalChatStreamRegistryEntry) {
    if (entry.evictionTimer === null) {
      return;
    }

    this.#clearTimer(entry.evictionTimer);
    entry.evictionTimer = null;
  }

  #deleteEntry(key: string) {
    const entry = this.#entries.get(key);
    if (!entry) {
      return;
    }

    this.#cancelEviction(entry);
    this.#entries.delete(key);

    if (this.#displayedEntry === entry) {
      this.#displayedEntry = null;
    }
  }
}

export const conversationChatRegistry = new ConversationChatRegistry();
